import type React from "react";

import {
  useToast,
  CloseButton,
  Button as ButtonCk,
  Spinner,
  VStack,
  HStack,
  Alert,
  AlertTitle,
  AlertDescription,
  Flex,
  Box,
} from "@chakra-ui/react";
import Button from "@components/common/Button"; 
import { OportunityContext } from "@context/oportunityContex";
import { useCallback, useContext } from "react";
import { theme } from "src/styles/theme";

interface MessageParams {
  title: string;
  message: string;
  titleButton?: string; // Changed to string for consistency
  onClick?: (e: React.FormEvent<HTMLButtonElement>) => void;
  loading?: boolean;
}

interface MessageDeleteParams {
  title: string;
  message: string;
  onClick: () => void;
  loading?: boolean;
}

interface IMessage {
  onClick: React.FormEventHandler<HTMLButtonElement>;
  title: string;
  message: string;
}

export const useMessageToast = () => {
  const toast = useToast();

  const displayMessage = useCallback(
    ({ title, message, titleButton, onClick }: MessageParams) => {
      toast.closeAll();
      toast({
        position: "top-right",
        status: undefined,
        duration: 5000,
        render: () => (
          <Alert
            status="info"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="auto"
            width="100%"
            padding="10px"
            borderRadius="8px"
            boxShadow={"2xl"}
            bg="white"
            color={theme.text}
          >
            <HStack
              width="100%"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <AlertTitle fontSize="md" fontWeight="bold" color={theme.text}>
                {title}
              </AlertTitle>
              <CloseButton onClick={() => toast.closeAll()} size="sm" />
            </HStack>

            <VStack spacing={1} width="100%" mt={2} mb={2}>
              {/* <AlertTitle fontSize="md" fontWeight="bold" color={theme.text}>
                {title}
              </AlertTitle> */}
              <AlertDescription fontSize="sm" color={theme.text}>
                {message}
              </AlertDescription>
            </VStack>

            {titleButton && onClick && (
              <ButtonCk
                mt={4}
                width="100%"
                bg={theme.primary}
                color="white"
                _hover={{ bg: theme.primary }}
                onClick={(e) => {
                  onClick(e);
                  toast.closeAll();
                }}
              >
                {titleButton}
              </ButtonCk>
            )}
          </Alert>
        ),
        containerStyle: {
          maxWidth: "350px",
          marginTop: "10px",
          boxShadow: "dark-lg",
          borderRadius: "8px",
          bg: "white",
        },
      });
    },
    [toast, theme]
  );

  return displayMessage;
};

export const useOnlyMessageToast = () => {
  const toast = useToast();

  const displayMessage = useCallback(
    (message: string) => {
      toast.closeAll();
      toast({
        position: "top-right",
        status: undefined,
        duration: 6000,
        render: () => (
          <Alert
            status="info"
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="auto"
            width="100%"
            padding="10px"
            pt={"10px"}
            pb={"10px"}
            borderRadius="8px"
            bg="white"
            color={theme.text}
          >
            <HStack
              display={"flex"}
              width="100%"
              justifyContent="space-between"
              align={"flex-start"}
            >
              <Flex align={"center"}>
                <AlertTitle fontSize="md" fontWeight="bold" color={theme.text}>
                  {message}
                </AlertTitle>
              </Flex>

              <Box display={"flex"} mt={"-4px"} w={"fit-content"}>
                <CloseButton onClick={() => toast.closeAll()} size="sm" />
              </Box>
            </HStack>
          </Alert>
        ),
        containerStyle: {
          maxWidth: "350px",
          marginTop: "10px",
          boxShadow: "dark-lg",
          borderRadius: "8px",
          bg: "white",
        },
      });
    },
    [toast, theme]
  );

  return displayMessage;
};

export const useMessageDeleteToast = () => {
  const toast = useToast();

  const Message = ({ onClick, title, message }: IMessage) => {
    const { loadingDeleteRelationship } = useContext(OportunityContext);

    return (
      <Alert
        status="warning"
        variant="solid"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="auto"
        width="100%"
        padding="10px"
        borderRadius="8px"
        bg="white"
        color={theme.text}
      >
        <HStack
          width="100%"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <AlertTitle fontSize="md" fontWeight="bold" color={theme.text}>
            {title}
          </AlertTitle>
          <CloseButton onClick={() => toast.closeAll()} size="sm" />
        </HStack>

        <VStack spacing={1} width="100%" mt={2} mb={2}>
          <AlertDescription fontSize="sm" color={theme.text}>
            {message}
          </AlertDescription>
        </VStack>

        <HStack width="100%" justifyContent="flex-end" mt={4} spacing={2}>
          <Button
            colorInverter
            title={"CANCELAR"}
            onClick={(e) => {
              toast.closeAll();
            }}
          />

          <Button
            colorError
            disabled={loadingDeleteRelationship}
            title={loadingDeleteRelationship ? <Spinner /> : "EXCLUIR"}
            onClick={onClick}
          />
        </HStack>
      </Alert>
    );
  };

  const displayMessage = ({ title, message, onClick }: MessageDeleteParams) => {
    toast.closeAll();
    toast({
      position: "top-right",
      status: undefined,
      duration: null, // No auto-close for delete confirmation
      render: () => (
        <Message onClick={onClick} title={title} message={message} />
      ),
      containerStyle: {
        maxWidth: "350px",
        marginTop: "10px",
        boxShadow: "dark-lg",
        borderRadius: "8px",
        bg: "white",
      },
    });
  };

  return displayMessage;
};
